import { useEffect, useMemo, useRef } from "react";

import { useProductsByIds } from "@src/graphql/hooks/useProductsByIds";
import { useDispatch, useSelector } from "@src/store";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";

import { mergeHydratedAnalyticsProducts } from "./merge-hydrated-analytics-products";

/**
 * The `selectedAnalyticsProducts` filter is persisted in the URL as numeric
 * product IDs only to prevent the URL from crashing. When a dashboard loads
 * from such a URL, those products arrive with a placeholder
 * (empty `handle`, null `title`). This hook fetches the missing product data
 * by ID and writes the resolved `title` (display) and `handle` (required by
 * the analytics payload) back into the filter state, dropping ids that can't be
 * resolved once the fetch completes.
 */
export const useHydrateAnalyticsProductFilters = () => {
  const dispatch = useDispatch();
  const selectedAnalyticsProducts = useSelector(
    (state) => state.dashboardFilters.selectedAnalyticsProducts
  );

  // The ids that still need title/handle resolved. Memoized so the array
  // reference is stable across renders for the same selection — useProductsByIds
  // keys an internal fetch effect off this array, so an unstable reference would
  // re-fetch (and re-render) on every render in a loop.
  const idsNeedingHydration = useMemo(
    () =>
      selectedAnalyticsProducts
        .filter((product) => !product.handle)
        .map((product) => product.id),
    [selectedAnalyticsProducts]
  );

  const { productDataById, loading } = useProductsByIds(idsNeedingHydration);

  // Tracks whether the previous render was mid-fetch, so we can tell "the fetch
  // just finished" (safe to drop unresolved ids) apart from "the fetch hasn't
  // started yet" (both look like loading === false with no data).
  const wasLoading = useRef(false);

  useEffect(() => {
    const fetchJustCompleted = wasLoading.current && !loading;
    wasLoading.current = loading;

    // Wait for the in-flight fetch to settle before touching the filter.
    if (loading || idsNeedingHydration.length === 0) {
      return;
    }

    // Drop still-unresolved ids only once a fetch has actually completed,
    // otherwise keep them (the fetch hasn't run yet).
    const hydrated = mergeHydratedAnalyticsProducts(
      selectedAnalyticsProducts,
      productDataById,
      fetchJustCompleted
    );

    const changed =
      hydrated.length !== selectedAnalyticsProducts.length ||
      hydrated.some(
        (product, index) => product !== selectedAnalyticsProducts[index]
      );

    if (changed) {
      dispatch(
        updateDashboardFilter({
          key: "selectedAnalyticsProducts",
          value: hydrated,
        })
      );
    }
  }, [
    loading,
    productDataById,
    selectedAnalyticsProducts,
    idsNeedingHydration,
    dispatch,
  ]);
};
